import React, { useContext, useCallback, useState, useMemo, useEffect, JSX } from 'react';
import { WorkflowContext } from "../context/WorkflowContextProvider";
import '@xyflow/react/dist/style.css';
import {
    ReactFlow,
    Controls,
    Background,
    BackgroundVariant,
    MiniMap,
    applyNodeChanges,
    applyEdgeChanges,
    useReactFlow,
    ReactFlowInstance,
    Node
} from '@xyflow/react';
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
    processAddNode,
    processEdgeConnections,
    workflowNodeItems,
    workflowNodeGroupLabels,
    accentForNodeType,
    WorkflowNodeItem,
    WorkflowNodeGroup
} from './workflow-builder';
import TuneIcon from '@mui/icons-material/Tune';
import OutputIcon from '@mui/icons-material/Output';
import {
    Drawer
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Custom Edges
import DeleteButtonEdge from "./components/edges/DeleteButtonEdge";

// Custom Nodes
import WorkflowNode from './components/nodes/WorkflowNode';
import WorkflowAgentNode from "./components/nodes/WorkflowAgentNode";
import WorkflowToolNode from "./components/nodes/WorkflowToolNode";
import WorkflowStartNode from "./components/nodes/WorkflowStartNode";
import WorkflowEndNode from "./components/nodes/WorkflowEndNode";
import WorkflowIfNode from "./components/nodes/WorkflowIfNode";
import WorkflowLoopNode from "./components/nodes/WorkflowLoopNode";
import WorkflowSitemapNode from "./components/nodes/WorkflowSitemapNode";
import WorkflowScraperNode from "./components/nodes/WorkflowScraperNode";
import WorkflowHQDataFetchNode from "./components/nodes/WorkflowHQDataFetchNode";
import WorkflowHQUpdateNode from "./components/nodes/WorkflowHQUpdateNode";
import WorkflowHTTPNode from "./components/nodes/WorkflowHTTPNode";
import WorkflowMergeNode from "./components/nodes/WorkflowMergeNode";
import WorkflowParallelNode from "./components/nodes/WorkflowParallelNode";
import WorkflowHumanNode from "./components/nodes/WorkflowHumanNode";
import WorkflowConvertNode from "./components/nodes/WorkflowConvertNode";
import WorkflowDataMoveNode from "./components/nodes/WorkflowDataMoveNode";
import WorkflowSummarizeNode from "./components/nodes/WorkflowSummarizeNode";
import WorkflowFilterNode from "./components/nodes/WorkflowFilterNode";
import WorkflowSwitchNode from "./components/nodes/WorkfowSwitchNode";
import WorkflowHQCreateNode from "./components/nodes/WorkflowHQCreateNode";
import WorkflowExtractionNode from "./components/nodes/WorkflowExtractionNode";
import WorkflowInjectionNode from "./components/nodes/WorkflowInjectionNode";
import WorkflowDataResetNode from "./components/nodes/WorkflowDataResetNode";
import WorkflowSortNode from "./components/nodes/WorkflowSortNode";
import WorkflowSampleDataNode from "./components/nodes/WorkflowSampleDataNode";
import WorkflowLabelNode from "./components/nodes/WorkflowLabelNode";
import WorkflowUiUpdateNode from "./components/nodes/WorkflowUIUpdateNode";
import WorkflowParallelScraperHelperNode from "./components/nodes/WorkflowParallelScraperHelperNode";
import WorkflowParallelScraperNode from "./components/nodes/WorkflowParallelScraperNode";

const nodeTypes = {
    workflowNode: WorkflowNode,
    workflowAgentNode: WorkflowAgentNode,
    workflowToolNode: WorkflowToolNode,
    workflowStartNode: WorkflowStartNode,
    workflowEndNode: WorkflowEndNode,
    workflowIfNode: WorkflowIfNode,
    workflowLoopNode: WorkflowLoopNode,
    workflowSitemapNode: WorkflowSitemapNode,
    workflowScraperNode: WorkflowScraperNode,
    workflowHQDataFetchNode: WorkflowHQDataFetchNode,
    workflowHQUploadNode: WorkflowHQUpdateNode,
    workflowHTTPNode: WorkflowHTTPNode,
    workflowMergeNode: WorkflowMergeNode,
    workflowParallelNode: WorkflowParallelNode,
    workflowHumanNode: WorkflowHumanNode,
    workflowConvertNode: WorkflowConvertNode,
    workflowMoveNode: WorkflowDataMoveNode,
    workflowSummarizeNode: WorkflowSummarizeNode,
    workflowFilterNode: WorkflowFilterNode,
    workflowSwitchNode: WorkflowSwitchNode,
    workflowHQCreateNode: WorkflowHQCreateNode,
    workflowExtractionNode: WorkflowExtractionNode,
    workflowInjectionNode: WorkflowInjectionNode,
    workflowDataResetNode: WorkflowDataResetNode,
    workflowSortNode: WorkflowSortNode,
    workflowSampleDataNode: WorkflowSampleDataNode,
    workflowLabelNode: WorkflowLabelNode,
    workflowUiUpdateNode: WorkflowUiUpdateNode,
    // workflowParallelScraperHelperNode: WorkflowParallelScraperHelperNode,
    // workflowParallelScraperNode: WorkflowParallelScraperNode
}

const edgeTypes = {
    deleteButtonEdge: DeleteButtonEdge
};

export const WorkflowBuilder: () => JSX.Element = (): JSX.Element => {
    const context: any = useContext(WorkflowContext);
    const reactFlowInstance: ReactFlowInstance = useReactFlow();
    const [showPanel, setShowPanel] = useState<boolean>(false);
    const [nodeQuery, setNodeQuery] = useState<string>('');

    // Everything else follows the theme through CSS custom properties. The
    // minimap cannot, because it paints node colours as SVG attributes.
    const paletteMode: 'light' | 'dark' = useTheme().palette.mode === 'dark' ? 'dark' : 'light';

    function handleControlToggle(): void {
        setShowPanel(!showPanel);
        setNodeQuery('');
    }

    function addNode(type: string, label: string): void  {
        setShowPanel(false);
        setNodeQuery('');
        processAddNode(type, label, context, reactFlowInstance);
    }

    // Filter first, then group, so an empty family disappears rather than
    // leaving a heading with nothing under it.
    const nodeGroups: [WorkflowNodeGroup, WorkflowNodeItem[]][] = useMemo(
        (): [WorkflowNodeGroup, WorkflowNodeItem[]][] => {
            const query: string = nodeQuery.trim().toLowerCase();
            const matches: WorkflowNodeItem[] = query.length === 0
                ? workflowNodeItems
                : workflowNodeItems.filter((item: WorkflowNodeItem): boolean =>
                    item.title.toLowerCase().includes(query) ||
                    item.group.toLowerCase().includes(query));

            const order: WorkflowNodeGroup[] =
                ['intelligence', 'acquisition', 'data', 'transform', 'flow', 'note'];

            return order
                .map((group: WorkflowNodeGroup): [WorkflowNodeGroup, WorkflowNodeItem[]] =>
                    [group, matches.filter((item: WorkflowNodeItem): boolean => item.group === group)])
                .filter(([, items]: [WorkflowNodeGroup, WorkflowNodeItem[]]): boolean => items.length > 0);
        },
        [nodeQuery]
    );

    const matchCount: number = nodeGroups.reduce(
        (total: number, [, items]: [WorkflowNodeGroup, WorkflowNodeItem[]]): number => total + items.length,
        0
    );

    const runningCount: number =
        (context?.testComplete?.length ?? 0) +
        (context?.testProcessing?.length ?? 0) +
        (context?.testErrored?.length ?? 0);

    function resetWorkflowTests(): void {
        context.clearTests();
    }

    function handleViewNodeOutput() : void {
        context.toggleShowTestOutputPanel(true);
    }

    const customOnNodesChange: (changes: any) => void = useCallback(
        (changes: any): void => {
            try {
                if (!Array.isArray(changes) || changes.length === 0) return;

                const isResizeOnly: boolean = changes.every(
                    (change: any): boolean =>
                        change.type === 'dimensions' &&
                        change.resizing === true // optional, depending on change object
                );

                const currentNodes: Node[] = reactFlowInstance.getNodes();

                // Apply changes immutably using React Flow's helper
                const updatedNodes: Node[] = applyNodeChanges(changes, currentNodes);
                if (isResizeOnly) {
                    // Add tiny delay if resizing as values change quickly and
                    // will throw a ResizeObserver error without.
                    // There might be a better fix?
                    setTimeout( (): void => {
                        context.updateLayoutNodes(updatedNodes);
                    }, 100 )
                } else {
                    context.updateLayoutNodes(updatedNodes);
                }
            } catch (error) {
                console.log(error);
            }
        },
        [context, reactFlowInstance],
    );


    const customOnEdgesChange: (changes: any) => void = useCallback(
        (changes: any): void => {
            if (!Array.isArray(changes) || changes.length === 0) return;

            const currentEdges = reactFlowInstance.getEdges();

            // Apply changes immutably
            const updatedEdges = applyEdgeChanges(changes, currentEdges);
            // Update your context-managed edge state
            context.updateLayoutEdges(updatedEdges);
        },
        [context, reactFlowInstance],
    );

    const onConnect: (params: any) => void = useCallback(
        (params: any): void => {
            processEdgeConnections(params, context, reactFlowInstance)
        },
        [],
    );

    function handleControlPanelToggle(): void {
        context.toggleShowControlsDrawer(true);
    }
   
    useEffect( (): () => void=> {
        const handleBeforeUnload: (e: any) => void = (e: any): void => {
            if (context !== undefined && context.operator !== undefined) {
                context.adjustOperatorAvailability(context.operator.id, 'down');
                // Optionally show confirmation dialog
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return (): void => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [context]);

    return (
        <>
            <div className="workflow-builder">
                { /* One rail: build at the top, inspect below the divider. */ }
                <div className="wf-rail">
                    <button type="button"
                            className={ "wf-rail__btn wf-rail__btn--primary" + ( showPanel ? ' is-open' : '' ) }
                            title={ showPanel ? "Close node palette" : "Add a node" }
                            aria-expanded={ showPanel }
                            onClick={ handleControlToggle }>
                        <AddIcon />
                    </button>
                    <span className="wf-rail__sep" />
                    <button type="button"
                            className={ "wf-rail__btn" + (context.interruptWaiting ? ' is-waiting' : '') }
                            title={ context.interruptWaiting ? "Waiting for input" : "Configuration and testing" }
                            onClick={ handleControlPanelToggle }>
                        <TuneIcon />
                    </button>
                    <button type="button"
                            className="wf-rail__btn"
                            title="Node output"
                            onClick={ handleViewNodeOutput }>
                        <OutputIcon />
                    </button>
                </div>

                { (runningCount > 0) &&
                    <button type="button" className="wf-clear-tests" onClick={ resetWorkflowTests }>
                        <span className="wf-dot" />
                        <span>{ "Clear " + runningCount + " result" + (runningCount === 1 ? "" : "s") }</span>
                    </button>
                }
                <ReactFlow
                    nodeTypes={ nodeTypes }
                    edgeTypes={ edgeTypes }
                    nodes={ context?.nodes }
                    onNodesChange={customOnNodesChange}
                    edges={ context?.edges }
                    onEdgesChange={customOnEdgesChange}
                    onConnect={ onConnect }
                    fitView
                    minZoom={0.1}
                    maxZoom={2}
                >
                    <Background variant={ BackgroundVariant.Dots } gap={ 22 } size={ 1.4 } />
                    <Controls showInteractive={ false } />
                    { /* Mask and background come from --xy-* in _canvas.scss so
                         the minimap follows the light/dark theme. */ }
                    <MiniMap pannable
                             zoomable
                             nodeColor={ (node: Node): string => accentForNodeType(node.type, paletteMode) }
                             nodeStrokeWidth={ 0 } />
                </ReactFlow>
            </div>

            <Drawer open={ showPanel }
                    anchor="left"
                    className="wf-picker-drawer"
                    onClose={ handleControlToggle } >
                <div className="wf-picker">
                    <header className="wf-picker__head">
                        <div>
                            <span className="wf-picker__eyebrow">{ "Palette" }</span>
                            <h2 className="wf-picker__title">{ "Add a node" }</h2>
                            <p className="wf-picker__hint">{ "It drops onto the canvas below your last node." }</p>
                        </div>
                        <button type="button"
                                className="wf-picker__close"
                                title="Close"
                                onClick={ handleControlToggle }>
                            <CloseIcon />
                        </button>
                    </header>

                    <div className="wf-picker__search">
                        <SearchIcon />
                        <input type="text"
                               value={ nodeQuery }
                               autoFocus
                               placeholder="Search nodes"
                               aria-label="Search nodes"
                               onChange={ (event) => setNodeQuery(event.target.value) } />
                    </div>

                    <div className="wf-picker__groups">
                        { nodeGroups.map( ([ group, items ]) => (
                            <section className="wf-picker__group" key={ "add_node_group_" + group } data-cat={ group }>
                                <h3 className="wf-picker__group-title">
                                    <span className="wf-picker__group-dot" />
                                    { workflowNodeGroupLabels[group] }
                                    <em className="wf-picker__group-count">{ items.length }</em>
                                </h3>
                                <div className="wf-picker__grid">
                                    { items.map( (node: WorkflowNodeItem) => (
                                        <button type="button"
                                                className="wf-picker__item"
                                                key={ "add_node_item_" + node.type }
                                                data-cat={ node.group }
                                                title={ node.title }
                                                onClick={ () => addNode(node.type, node.label) }>
                                            <span className="wf-glyph" data-node={ node.slug } aria-hidden="true" />
                                            <span className="wf-picker__item-name">{ node.title }</span>
                                        </button>
                                    ) ) }
                                </div>
                            </section>
                        ) ) }

                        { (matchCount === 0) &&
                            <p className="wf-picker__empty">
                                { "No node matches " }<strong>{ nodeQuery }</strong>{ "." }<br />
                                { "Try a family name such as “transform” or “flow”." }
                            </p>
                        }
                    </div>
                </div>
            </Drawer>
        </>
    );
}